import { EventInput } from '@fullcalendar/core';
import { addMilliseconds, endOfDay, isSameDay, startOfDay } from 'date-fns';
import { halfHour } from 'shared/constants';
import { isBetween } from 'shared/lib/date-fns';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { Todo } from 'shared/types/todo';
import { WeekDays } from 'shared/types/week-days';
import { formatIdDateKey } from 'shared/utils/format-id-date-key';
import {
  getCalendarTaskEndDate,
  getCalendarTaskStartDate,
} from 'shared/utils/get-calendar-task-dates';
import { getTaskPlanning } from 'shared/utils/get-task-planning';
import { isNumber } from 'shared/utils/is-number';
import { getTimeframeSchedules } from 'shared/utils/schedules';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
  today: Timestamp;
  weekStartsOn: WeekDays;
  hideCompleted?: boolean;
};

export const getTasksEventsForDateRange = (
  tasks: Task[],
  { startDate, endDate, today, weekStartsOn, hideCompleted }: Options,
) =>
  tasks.reduce<EventInput[]>((acc, task) => {
    if (![TaskType.Habit, TaskType.Repeating].includes(task.type)) {
      if (hideCompleted && task.completedAt) {
        return acc;
      }

      acc.push({
        id: task.id,
        date: task.endStrategy?.deadline ?? undefined,
        start: getCalendarTaskStartDate(task),
        end: getCalendarTaskEndDate(task),
        title: task.name,
        allDay: !isNumber((task as Todo).timeslot?.startTime),
        extendedProps: {
          taskId: task.id,
          duration: (task as Todo).timeslot?.duration || halfHour,
        },
      });

      if (task.deadlineDeadline) {
        acc.push({
          id: formatIdDateKey(task.id, task.deadlineDeadline),
          date: task.deadlineDeadline,
          title: task.name,
          allDay: true,
          extendedProps: {
            taskId: task.id,
            isDeadline: true,
          },
        });
      }

      return acc;
    }

    // habit
    if (!task.schedules?.length || task.hideOnTodo) {
      return acc;
    }

    const timeframeSchedules = getTimeframeSchedules(task.schedules, {
      startDate,
      endDate,
    });
    if (!timeframeSchedules.length) {
      return acc;
    }

    const plannedOccurrences = getTaskPlanning(task, {
      weekStartsOn,
      startDate: startOfDay(startDate),
      endDate: endOfDay(endDate),
      today,
      uncompletedOnly: hideCompleted,
    });

    // Add planned occurrences (which might have specific times)
    for (const plannedOccurrence of plannedOccurrences) {
      const duration = plannedOccurrence.timeslot?.duration ?? halfHour;
      const start = isNumber(plannedOccurrence.timeslot?.startTime)
        ? addMilliseconds(
            startOfDay(plannedOccurrence.date),
            plannedOccurrence.timeslot!.startTime!,
          )
        : undefined;
      const end = start
        ? addMilliseconds(
            start,
            // 30 minutes is the default duration if no duration is set
            duration,
          )
        : undefined;

      acc.push({
        id: formatIdDateKey(task.id, start ?? plannedOccurrence.date, duration),
        date: plannedOccurrence.date,
        start,
        end,
        title: task.name,
        allDay: !start || !end,
        extendedProps: {
          task,
          taskId: task.id,
          weekStartsOn,
          occurrenceId: plannedOccurrence.id,
          duration,
        },
      });
    }

    // Add All-Day entries for days that have an entry (completion, skip, failure)
    // but are NOT already covered by a planned occurrence (meaning they don't have a specific time).
    for (const schedule of task.schedules) {
      for (const entry of schedule.entries) {
        if (!isBetween(entry.date, startOfDay(startDate), endOfDay(endDate))) {
          continue;
        }

        // Check if this entry is already represented by a planned occurrence on the same day
        const hasPlannedOnDay = plannedOccurrences.some((po) =>
          isSameDay(po.date, entry.date),
        );

        if (!hasPlannedOnDay) {
          acc.push({
            id: formatIdDateKey(task.id, entry.date, halfHour),
            date: entry.date,
            title: task.name,
            allDay: true,
            extendedProps: {
              task,
              taskId: task.id,
              weekStartsOn,
              duration: halfHour,
            },
          });
        }
      }
    }

    return acc;
  }, []);
